import React from 'react';
import styles from './ui.module.css';
import Icon from './Icon';

export type NoteTone = 'error' | 'warning' | 'info' | 'success';

interface NoteProps {
  tone?: NoteTone;
  /** Trailing control — a Retry or Reload button. */
  action?: React.ReactNode;
  /** Shows a close control; the caller clears the message. */
  onDismiss?: () => void;
  children: React.ReactNode;
}

const TONE = {
  error: { cls: styles.noteError, icon: 'alert' as const },
  warning: { cls: styles.noteWarn, icon: 'alert' as const },
  info: { cls: styles.noteInfo, icon: 'info' as const },
  success: { cls: styles.noteSuccess, icon: 'check' as const },
};

/**
 * An inline message about the page's data — a rejected file, a stale figure,
 * an action that just succeeded. Not for field validation, which belongs next
 * to the field. `success` is green because it reports a positive outcome.
 */
const Note: React.FC<NoteProps> = ({ tone = 'info', action, onDismiss, children }) => {
  const t = TONE[tone];
  return (
    <div className={`${styles.noteBar} ${t.cls}`} role={tone === 'error' ? 'alert' : undefined}>
      <Icon name={t.icon} size={15} />
      <span className={styles.noteBody}>{children}</span>
      {action && <span className={styles.noteAction}>{action}</span>}
      {onDismiss && (
        <button type="button" className={styles.noteDismiss} onClick={onDismiss} aria-label="Dismiss">
          <Icon name="close" size={14} />
        </button>
      )}
    </div>
  );
};

export default Note;
